import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "@/Functions";
import { getAdmissionProcessAPI, createApplicationAPI } from "@/Data/Admission";
import { uploadImageToS3 } from "@/Data/Image";
import { submitApplicationDocumentAPI } from "@/Data/Admission/ApplicationDocument";
import type { University, Track, Application, Document, Step, Requirement, ApplicationDocument } from 'ai-api/types/admission';

export type AdmissionProcess = {
    university: University;
    track: Track;
    application?: Application;
    documents: Document[];
    steps: (Step & {
        sections: {
            section: string;
            requirements: (Requirement & {
                documents: (Document & {submitted?: ApplicationDocument})[];
            })[];
        }[]
        completed: boolean;
    })[];
};

// Main state atom for admission process
const admissionProcessState = atom({
    key: 'admissionProcessState' + randomString(4),
    default: {
        process: null as AdmissionProcess | null,
        loading: false,
        error: null as string | null,
    }
});

/**
 * Hook for managing admission process
 */
export const useAdmissionProcess = () => {
    const [state, setState] = useRecoilState(admissionProcessState);
    const overwriteState = useCallback((newState: Partial<typeof state>) => setState(prev => ({ ...prev, ...newState })), [setState]);

    const { process, loading, error } = state;

    // Sync admission process from API
    const syncProcess = useCallback(async (universityid: string, trackid: string, applicationid?: string): Promise<void> => {
        if (!universityid || !trackid) {
            console.warn('University ID and Track ID are required');
            return;
        }

        overwriteState({ loading: true, error: null });

        try {
            const processData = await getAdmissionProcessAPI({ universityid, trackid, applicationid });
            
            if (processData) {
                setState(prev => ({
                    ...prev,
                    process: processData,
                    loading: false
                }));
            } else {
                overwriteState({ 
                    loading: false,
                    error: "No admission process found"
                });
            }
            
        } catch (error) {
            console.error('Error fetching admission process:', error);
            overwriteState({
                error: error instanceof Error ? error.message : "Failed to fetch admission process",
                loading: false
            });
        }
    }, [setState, overwriteState]);

    // Create application function
    const createApplication = useCallback(async (universityid: string, trackid: string, applicationid: string): Promise<boolean> => {
        try {
            const success = await createApplicationAPI({
                applicationid,
                universityid,
                trackid,
                status: 'draft',
                notes: 'Application created'
            });

            if (success) {
                // Refresh process data to include the new application
                await syncProcess(universityid, trackid, applicationid);
            }

            return success;
        } catch (error) {
            console.error('Application creation error:', error);
            return false;
        }
    }, [syncProcess]);

    // Upload document function
    const uploadDocument = useCallback(async (file: File, documentid: string, userid: string): Promise<boolean> => {
        if (!process?.application?.applicationid) {
            console.error('No application found for document upload');
            return false;
        }

        try {
            // Create object URL for the file
            const objectURL = URL.createObjectURL(file);
            
            // Upload to S3
            const s3Url = await uploadImageToS3(objectURL, file.name);
            
            // Clean up object URL
            URL.revokeObjectURL(objectURL);

            if (!s3Url) {
                console.error('Failed to upload file to S3');
                return false;
            }

            // Submit document to admission API
            const success = await submitApplicationDocumentAPI({
                applicationid: process.application.applicationid,
                documentid,
                userid,
                file: s3Url,
                note: `${file.name} uploaded via S3`
            });

            return success;
        } catch (error) {
            console.error('Document upload error:', error);
            return false;
        }
    }, [process?.application?.applicationid]);

    const reset = useResetRecoilState(admissionProcessState);

    return {
        // Data
        process,
        loading,
        error,

        // Actions
        syncProcess,
        createApplication,
        uploadDocument,
        reset
    };
};